import type { useTranslations } from 'next-intl';
import type { JourneyTransformed } from '~/types/Journey.types';
import type { StayTransformed } from '~/types/JourneyItem.types';

// A one-night stop is usually just a stopover, not somewhere worth naming in the trip title
// (e.g. a 4-stop trip "Zürich(1) → Bologna(3) → Sanremo(3) → Parijs(1)" is titled "A journey
// through Bologna & Sanremo" by JAI, skipping the 1-night stops). Falls back to every stay if
// none clears the bar, so the title is never empty.
const MIN_HIGHLIGHT_NIGHTS = 2;

export function getTripHighlightPlaceNames(stays: StayTransformed[]): string[] {
  const highlights = stays.filter((stay) => stay.nights >= MIN_HIGHLIGHT_NIGHTS);
  return (highlights.length > 0 ? highlights : stays).map((stay) => stay.title);
}

export function joinPlaceNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

// Trips built from a Blueprint or Virtual Blueprint keep their own CMS-authored title rather than
// being rebuilt from region/place data below. 'FROM_BLUEPRINT_TRIP' is how `getAndTransformTemplate`
// relabels 'FROM_TEMPLATE' for the /journey-template preview specifically (see engine-ts.ts).
const BLUEPRINT_CREATION_SOURCES = [
  'FROM_TEMPLATE',
  'FROM_BLUEPRINT_TRIP',
  'FROM_VIRTUAL_BLUEPRINT',
  'FROM_VIRTUAL_BLUEPRINT_TEMPLATE'
];

// Only 'FROM_TEMPLATE' (the standard Blueprint CMS model) has a per-record title we can re-fetch
// by id (see queries/blueprints.ts). Virtual Blueprint titles come from a locale-scoped generic-
// content lookup keyed by trip length, not a single field we can look up here.
const REFETCHABLE_CREATION_SOURCE = 'FROM_TEMPLATE';

// English locales show the engine's title and intro text as-is (they're English already, and this
// keeps manual Journey Editor edits visible). Other locales rebuild them from CMS-localised pieces.
const isEnglishLocale = (locale: string) => locale.startsWith('en');

type TripTitleSource = Pick<JourneyTransformed, 'title' | 'region' | 'source' | 'items'>;

// originalBlueprintId is the Dato Blueprint's id. previousIds is the engine's copy chain, so its
// last entry can be an intermediate template copy that Dato doesn't know (the title lookup then
// returns null and the frozen, un-localised title wins). It's only a fallback for older trips.
export function getRefetchableBlueprintId(trip: TripTitleSource): string | undefined {
  if (trip.source?.creationSource !== REFETCHABLE_CREATION_SOURCE) return undefined;
  return trip.source.originalBlueprintId ?? trip.source.previousIds?.at(-1);
}

// The engine returns `title` as a fully-formed sentence that next-intl never touches. Blueprint
// trips bake in the Blueprint's CMS title (already locale-aware) at creation time, so it goes
// stale if the trip is later viewed in a different locale - callers re-fetch it fresh (via
// getRefetchableBlueprintId) and pass it in as `blueprintTitle` instead of this trusting the
// frozen one. Non-blueprint trips (e.g. "Your journey to Italy") aren't backed by any CMS record
// at all, so we rebuild the sentence instead:
// - A trip with a single marketed region gives us a CMS-localised region name to slot into
//   "Your journey to {destination}". `region.id` falsy is the engine's sentinel for "no marketed
//   region" (multi-stop/cross-border trips, e.g. most Maps/Playground trips) - resolveRegionContent
//   deliberately skips CMS lookup for it, so `region.title` is the raw, un-localised engine value
//   and can't be trusted here (see regionCmsEnrichment.ts).
// - Without a usable region, we fall back to the same "A journey through {places}" phrasing,
//   built from CMS-localised stay place names.
//
// English locales skip all of the above and use the engine title unchanged.
//
// Shared by the client display title (useTripDisplayTitle) and the page's <title>/OG tag
// (generateMetadata) so the two can't drift apart: `t` has the same call signature whether it
// comes from next-intl's useTranslations() or its server-side getTranslations().
export function resolveTripDisplayTitle(
  t: ReturnType<typeof useTranslations>,
  locale: string,
  trip: TripTitleSource,
  blueprintTitle?: string | null
): string {
  if (isEnglishLocale(locale)) return blueprintTitle || trip.title;

  if (BLUEPRINT_CREATION_SOURCES.includes(trip.source?.creationSource ?? '')) {
    return blueprintTitle || trip.title;
  }

  if (trip.region?.id && trip.region.title) {
    const destination = [trip.region.article, trip.region.title].filter(Boolean).join(' ');
    return t('journey.trip_title', { destination });
  }

  const stays = trip.items.filter((item) => item.type === 'Stay');
  if (stays.length > 0) {
    const places = joinPlaceNames(getTripHighlightPlaceNames(stays));
    return t('playground.trip_title_journey', { places });
  }

  return trip.title;
}

// The engine's `intro_text` is an English sentence built server-side (HybridTripTransformer), so it
// can't be localised there (English locales use it as-is). Rebuild it from the same CMS-localised pieces as the title, using every
// Stay and Stop (the transformer's `place` list) in itinerary order. Falls back to the engine text
// when there are no places to list (e.g. a point-to-point "Your trip from X to Y" journey). Featured
// activity descriptions are appended exactly as the transformer does.
export function resolveTripIntroText(
  t: ReturnType<typeof useTranslations>,
  locale: string,
  trip: Pick<JourneyTransformed, 'intro_text' | 'region' | 'items' | 'featuredActivities'>
): string {
  if (isEnglishLocale(locale)) return trip.intro_text;

  const places = trip.items.filter((item) => item.type === 'Stay' || item.type === 'Stop').map((item) => item.title);
  if (places.length === 0) return trip.intro_text;

  const activities = trip.featuredActivities.map((activity) => ` ${activity.longDescription}`).join('');
  const placeNames = joinPlaceNames(places);

  if (trip.region?.id && trip.region.title) {
    const destination = [trip.region.article, trip.region.title].filter(Boolean).join(' ');
    return `${t('journey.intro_text_destination', { destination, places: placeNames })}${activities}`;
  }
  return `${t('journey.intro_text', { places: placeNames })}${activities}`;
}
